import { useQuery } from '@tanstack/react-query';
import { coffreApi } from '../../api/coffre.js';
import { mediasApi } from '../../api/medias.js';
import { cleMetier, appelDeRequete } from '../../api/cles.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, PriceInput, Select } from '@artosera/ui';
import {
  CATEGORIES_DEPENSE,
  type CategorieDepense,
  type CorpsDepense,
  type Depense,
  type LigneBancaire,
  messageTresorerie,
  useCreerDepense,
  useModifierDepense,
  useDepenseDepuisLigne,
} from '../../api/tresorerie.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import { Montant, aujourdhui, dateParis, DateJour } from './commun.js';
import styles from './Tresorerie.module.css';
export function DepenseFormulaire({
  depense,
  ligne,
  onClose,
}: {
  depense?: Depense;
  ligne?: LigneBancaire;
  onClose: () => void;
}) {
  const { galerieSlug, galerie } = useShellContext();
  const { t, i18n } = useTranslation('regie');
  const creer = useCreerDepense(galerieSlug);
  const modifier = useModifierDepense(galerieSlug);
  const depuisLigne = useDepenseDepuisLigne(galerieSlug);
  const [libelle, setLibelle] = useState(depense?.libelle ?? ligne?.libelle ?? '');
  const [fournisseurNom, setFournisseur] = useState(
    depense?.fournisseurNom ?? ligne?.contrepartie ?? '',
  );
  const [categorie, setCategorie] = useState<CategorieDepense>(depense?.categorie ?? 'autre');
  const [montant, setMontant] = useState(depense?.montantCentimes ?? 0);
  const [date, setDate] = useState(depense ? dateParis(depense.date) : aujourdhui());
  const [echeanceLe, setEcheance] = useState(
    depense ? dateParis(depense.echeanceLe) : aujourdhui(),
  );
  const [note, setNote] = useState(depense?.note ?? '');
  const [typePiece, setTypePiece] = useState<'document' | 'media'>(
    depense?.piece?.type ??
      (galerieAModule(galerie, 'coffre') && galerieALaction(galerie, 'documents:read')
        ? 'document'
        : 'media'),
  );
  const [pieceId, setPieceId] = useState(depense?.piece?.id ?? '');
  const [recherchePiece, setRecherchePiece] = useState('');
  const avecCoffre =
    galerieAModule(galerie, 'coffre') && galerieALaction(galerie, 'documents:read');
  const avecMedias = galerieALaction(galerie, 'media:read');
  const documents = useQuery({
    queryKey: cleMetier(galerieSlug, 'coffre', 'tresorerie', recherchePiece),
    queryFn: (c) =>
      coffreApi.lister(
        galerieSlug,
        { q: recherchePiece || undefined, parPage: 200 },
        appelDeRequete(c),
      ),
    enabled: avecCoffre && typePiece === 'document',
  });
  const medias = useQuery({
    queryKey: cleMetier(galerieSlug, 'medias', 'galerie'),
    queryFn: (c) => mediasApi.listerGalerie(galerieSlug, appelDeRequete(c)),
    enabled: avecMedias && typePiece === 'media',
  });
  const choixPieces =
    typePiece === 'document'
      ? (documents.data?.documents ?? []).map((d) => ({ value: d.id, label: d.titre }))
      : (medias.data?.medias ?? [])
          .filter((m) =>
            m.nomFichier.toLocaleLowerCase().includes(recherchePiece.toLocaleLowerCase()),
          )
          .map((m) => ({ value: m.id, label: m.nomFichier }));
  const [erreur, setErreur] = useState('');
  const label = (c: string) => t(`tresorerie.${c}`);
  const occupe = creer.isPending || modifier.isPending || depuisLigne.isPending;
  async function soumettre() {
    setErreur('');
    const commun = {
      libelle,
      fournisseurNom,
      categorie,
      note,
      ...(pieceId ? { piece: { type: typePiece, id: pieceId } } : {}),
    };
    try {
      if (ligne) await depuisLigne.mutateAsync({ id: ligne.id, corps: commun });
      else {
        const corps: CorpsDepense = { ...commun, montantCentimes: montant, date, echeanceLe };
        if (depense)
          await modifier.mutateAsync({
            id: depense.id,
            corps: { ...corps, versionAttendue: depense.version },
          });
        else await creer.mutateAsync(corps);
      }
      onClose();
    } catch (e) {
      setErreur(messageTresorerie(e));
    }
  }
  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        void soumettre();
      }}
    >
      <Field htmlFor="depense-libelle" label={label('libelle')}>
        <Input
          id="depense-libelle"
          required
          value={libelle}
          onChange={(e) => setLibelle(e.target.value)}
        />
      </Field>
      <Field htmlFor="depense-fournisseur" label={label('fournisseur')}>
        <Input
          id="depense-fournisseur"
          value={fournisseurNom}
          onChange={(e) => setFournisseur(e.target.value)}
        />
      </Field>
      <Field htmlFor="depense-categorie" label={label('categorie')}>
        <Select
          id="depense-categorie"
          value={categorie}
          onValueChange={(v) => setCategorie(v as CategorieDepense)}
          options={CATEGORIES_DEPENSE.map((v) => ({ value: v, label: label(`categories.${v}`) }))}
        />
      </Field>
      {ligne ? (
        <p>
          <DateJour date={ligne.dateOperation} /> · <Montant cents={ligne.montantCentimes} />
        </p>
      ) : (
        <>
          <Field htmlFor="depense-montant" label={label('montant')}>
            <PriceInput
              id="depense-montant"
              cents={montant}
              onCentsChange={setMontant}
              lang={i18n.language === 'en' ? 'en' : 'fr'}
            />
          </Field>
          <Field htmlFor="depense-date" label={label('date')}>
            <Input
              id="depense-date"
              type="date"
              required
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </Field>
          <Field htmlFor="depense-echeance" label={label('echeance')}>
            <Input
              id="depense-echeance"
              type="date"
              required
              value={echeanceLe}
              onChange={(e) => setEcheance(e.target.value)}
            />
          </Field>
        </>
      )}
      {(avecCoffre || avecMedias) && (
        <>
          <Field htmlFor="depense-type-piece" label={label('typeJustificatif')}>
            <Select
              id="depense-type-piece"
              value={typePiece}
              onValueChange={(v) => {
                setTypePiece(v as 'document' | 'media');
                setPieceId('');
              }}
              options={[...(avecCoffre ? ['document'] : []), ...(avecMedias ? ['media'] : [])].map(
                (v) => ({ value: v, label: label(v) }),
              )}
            />
          </Field>
          <Field htmlFor="depense-recherche-piece" label={label('rechercheJustificatif')}>
            <Input
              id="depense-recherche-piece"
              value={recherchePiece}
              onChange={(e) => setRecherchePiece(e.target.value)}
            />
          </Field>
          <Field htmlFor="depense-piece" label={label('justificatif')}>
            <Select
              id="depense-piece"
              value={pieceId}
              onValueChange={setPieceId}
              options={[{ value: '', label: label('sansJustificatif') }, ...choixPieces]}
            />
          </Field>
          {(documents.isError || medias.isError) && (
            <p role="alert">{messageTresorerie(documents.error ?? medias.error)}</p>
          )}
        </>
      )}
      <Field htmlFor="depense-note" label={label('note')}>
        <Input id="depense-note" value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
      {erreur && <p role="alert">{erreur}</p>}
      <Button type="submit" loading={occupe} disabled={!libelle.trim() || (!ligne && montant <= 0)}>
        {label('enregistrerDepense')}
      </Button>
    </form>
  );
}
